import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpModule, Http } from '@angular/http';
import { registerLocaleData, DatePipe } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';

import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { TranslateModule, TranslateLoader, TranslateStaticLoader } from 'ng2-translate/ng2-translate';
import { FlexLayoutModule } from '@angular/flex-layout';
import localEs from '@angular/common/locales/es';

import { ToastrModule } from 'ngx-toastr';
import { FileUploadModule } from 'ng2-file-upload';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { NgxCaptchaModule } from 'ngx-captcha';
import { PdfViewerModule } from 'ng2-pdf-viewer';

import { DemoMaterialModule } from './shared/demo.module';

import {MatSidenavModule} from '@angular/material/sidenav';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatListModule} from '@angular/material/list';
import {MatMenuModule} from '@angular/material/menu';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatTabsModule} from '@angular/material/tabs';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatSelectModule} from '@angular/material/select';
import {MatCardModule} from '@angular/material/card';

import { AppRoutes } from './app.routing';
import { AppComponent } from './app.component';
import { AdminLayoutComponent } from './layouts/admin/admin-layout.component';
import { SharedModule } from './shared/shared.module';

import { EnsureAuthenticatedService } from './services/ensure-authenticated.service';
import { LoginRedirectService } from './services/login-redirect.service';
import { AppConfig } from './services/constants';
import { GeneralService } from './services/general.service';
import { LoginService } from './services/login.service';
import {UsersService} from './services/users.service';
import {ProvidersService} from './services/providers.service';
import {GroupService} from './services/group.service';

import { LoginComponent } from './auth/login/login.component';
import { RecoverComponent } from './auth/recover/recover.component';
import { InvoiceReceptionComponent } from './receptions/invoice-reception/invoice-reception.component';
import { InvoicesReportComponent } from './reports/invoices-report/invoices-report.component';
import { OrdersReportComponent } from './reports/orders-report/orders-report.component';
import { PaymentsReportComponent } from './reports/payments-report/payments-report.component';
import { FaqsComponent } from './help/faqs/faqs.component';
import { ManualUserComponent } from './help/manual-user/manual-user.component';
import { HomeComponent } from './home/home.component';
import { ModalConfirmComponent } from './modal-confirm/modal-confirm.component';
import { ProviderDetailComponent } from './admin/providers/provider-detail/provider-detail.component';
import { ProviderComponent } from './admin/providers/provider/provider.component';
import { ProvidersComponent } from './admin/providers/providers/providers.component';
import { UsersComponent } from './admin/users/users/users.component';
import { UserComponent } from './admin/users/user/user.component';
import { UserDetailComponent } from './admin/users/user-detail/user-detail.component';
import { CompaniesComponent } from './admin/companies/companies/companies.component';
import { CompanyComponent } from './admin/companies/company/company.component';
import { WorkflowsComponent } from './admin/workflow/workflows/workflows.component';
import { WorkflowComponent } from './admin/workflow/workflow/workflow.component';
import { WorkflowDetailComponent } from './admin/workflow/workflow-detail/workflow-detail.component';
import { GeneralComponent } from './admin/general/general/general.component';
import { OfficesComponent } from './admin/offices/offices/offices.component';
import { OfficeComponent } from './admin/offices/office/office.component';
import { DepartamentsComponent } from './admin/departaments/departaments/departaments.component';
import { DepartamentComponent } from './admin/departaments/departament/departament.component';
import { ModalDetailComponent } from './modal-detail/modal-detail.component';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { OpenOrdersComponent } from './orders/open-orders/open-orders.component';
import { CloseOrdersComponent } from './orders/close-orders/close-orders.component';
import { SystemComponent } from './system/system/system.component';
import { ConsignmentsComponent } from './consignment/consignments/consignments.component';
import { ServicesComponent } from './service/services/services.component';
import { ApproverServicesComponent } from './service/approver-services/approver-services.component';
import { ApproverServicesDetailComponent } from './service/approver-services-detail/approver-services-detail.component';
import { ModalInvoicePdfComponent } from './modal-invoice-pdf/modal-invoice-pdf.component';
import { InvoicesPaymentsComponent } from './payments/invoices-payments/invoices-payments.component';
import { ManualPaymentsComponent } from './payments/manual-payments/manual-payments.component';
import { ComplementPaymentsComponent } from './payments/complement-payments/complement-payments.component';
import { ComplementPaymentsDetailComponent } from './payments/complement-payments-detail/complement-payments-detail.component';
import { ComplementHistoryComponent } from './payments/complement-history/complement-history.component';
import { ModalUploadComponent } from './modal-upload/modal-upload.component';
import { ServicesListComponent } from './service/services-list/services-list.component';
import { CloseOrdersListComponent } from './orders/close-orders-list/close-orders-list.component';
import {CloseOrdersListReportComponent} from './orders/close-orders-list-report/close-orders-list-report.component';
import { OpenOrdersListComponent } from './orders/open-orders-list/open-orders-list.component';
import { OrderDetailComponent } from './orders/order-detail/order-detail.component';
import { CreditNoteComponent } from './credit-notes/credit-note/credit-note.component';
import { ProviderModalComponent } from './admin/providers/provider-modal/provider-modal.component';
import { AuthorizersComponent } from './admin/authorizers/authorizers/authorizers.component';
import { AuthorizersDetailComponent } from './admin/authorizers/authorizers-detail/authorizers-detail.component';
import { RetentionsComponent } from './service/retentions/retentions.component';
import { RetentionsDetailComponent } from './service/retentions-detail/retentions-detail.component';
import { CostCenterComponent } from './admin/cost-center/cost-center.component';
import { CostCenterDetailComponent } from './admin/cost-center-detail/cost-center-detail.component';
import { CompleteDataGeneralComponent } from './admin/users/complete-data-general/complete-data-general.component';
import {MatStepperModule} from '@angular/material/stepper';
import { DeviceDetectorModule } from 'ngx-device-detector';
import { ContactComponent } from './contact/contact.component';
import { ModalErrorPaymentComponent } from './payments/modal-error-payment/modal-error-payment.component';
import { ComunicadosComponent } from './admin/comunicados/comunicados.component';
import { ModalComunicadoComponent } from './modal-comunicado/modal-comunicado.component';
import { ModalComunicadosShowComponent } from './admin/comunicados-show/comunicados-show';
import { PoliticasComponent } from './admin/general/politicas/politicas.component';
import { BulkLoadComponent } from './payments/bulk-load/bulk-load.component';
import { ModalBulkLoadComponent } from './modal-bulk-load/modal-bulk-load.component';
import { NoteListComponent } from './credit-notes/note-list/note-list.component';
import { NoteSearchComponent } from './credit-notes/note-search/note-search.component';
import { ManualListComponent } from './manual-payments/manual-list/manual-list.component';
import { ManualSearchComponent } from './manual-payments/manual-search/manual-search.component';
import { ApproveComponent } from './orders/approve-orders/approve.component';
import { ReportConsigmentsComponent } from './consignment/report-consigments/report-consigments.component';
import { ProviderCompaniesComponent } from './admin/providers/provider-companies/provider-companies.component';
import { NgxExtendedPdfViewerModule} from 'ngx-extended-pdf-viewer';
import { ManualComponent } from './admin/general/manuales/manual.component';
import { DetailsPaymentsComponent } from './manual-payments/details-payments/details-payments.component';


export function createTranslateLoader(http: Http) {
  return new TranslateStaticLoader(http, './assets/i18n', '.json');
}

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true
};

registerLocaleData(localEs);

@NgModule({
  declarations: [
    AppComponent,
    AdminLayoutComponent,
    LoginComponent,
    RecoverComponent,
    InvoiceReceptionComponent,
    InvoicesReportComponent,
    OrdersReportComponent,
    PaymentsReportComponent,
    FaqsComponent,
    ManualUserComponent,
    HomeComponent,
    ModalConfirmComponent,
    ProviderDetailComponent,
    ProviderComponent,
    ProvidersComponent,
    UsersComponent,
    UserComponent,
    UserDetailComponent,
    CompaniesComponent,
    CompanyComponent,
    WorkflowsComponent,
    WorkflowComponent,
    WorkflowDetailComponent,
    GeneralComponent,
    OfficesComponent,
    OfficeComponent,
    DepartamentsComponent,
    DepartamentComponent,
    ModalDetailComponent,
    OpenOrdersComponent,
    CloseOrdersComponent,
    SystemComponent,
    ConsignmentsComponent,
    ServicesComponent,
    ApproverServicesComponent,
    ApproverServicesDetailComponent,
    ModalInvoicePdfComponent,
    InvoicesPaymentsComponent,
    ManualPaymentsComponent,
    ComplementPaymentsComponent,
    ComplementPaymentsDetailComponent,
    ComplementHistoryComponent,
    ModalUploadComponent,
    ServicesListComponent,
    CloseOrdersListComponent,
    CloseOrdersListReportComponent,
    OpenOrdersListComponent,
    OrderDetailComponent,
    CreditNoteComponent,
    ProviderModalComponent,
    AuthorizersComponent,
    AuthorizersDetailComponent,
    RetentionsComponent,
    RetentionsDetailComponent,
    CostCenterComponent,
    CostCenterDetailComponent,
    CompleteDataGeneralComponent,
    ContactComponent,
    ModalErrorPaymentComponent,
    ComunicadosComponent,
    ModalComunicadosShowComponent,
    ModalComunicadoComponent,
    PoliticasComponent,
    BulkLoadComponent,
    ModalBulkLoadComponent,
    NoteListComponent,
    NoteSearchComponent,
    ManualListComponent,
    ManualSearchComponent,
    ApproveComponent,
    ReportConsigmentsComponent,
    ProviderCompaniesComponent,
    ManualComponent,
    DetailsPaymentsComponent

  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    RouterModule.forRoot(AppRoutes),
    FormsModule,
    ReactiveFormsModule,
    HttpModule,
    HttpClientModule,
    MatSidenavModule,
    MatInputModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatToolbarModule,
    MatTabsModule,
    MatCheckboxModule,
    MatProgressBarModule,
    MatSelectModule,
    MatCardModule,
    ToastrModule.forRoot({enableHtml: true}),
    FileUploadModule,
    LoadingBarHttpClientModule,
    LoadingBarRouterModule,
    DeviceDetectorModule.forRoot(),
    LoadingBarModule.forRoot(),
    NgxCaptchaModule.forRoot({
      reCaptcha2SiteKey: '6Ld6WZMbAAAAALLwzG6nJvHwLaPmroxHnBGuq3aq'//'6LeUnF8UAAAAAAiyQ_14h5R9DLW_mX1Ru1wWb2Me'
    }),
    PdfViewerModule,
    NgxDatatableModule,
    DemoMaterialModule,
    TranslateModule.forRoot({
      provide: TranslateLoader,
      useFactory: (createTranslateLoader),
      deps: [Http]
    }),
    MatStepperModule,
    FlexLayoutModule,
    NgxExtendedPdfViewerModule,
  ],
  providers: [
    GeneralService,
    UsersService,
    ProvidersService,
    GroupService,
    LoginService,
    EnsureAuthenticatedService,
    LoginRedirectService,
    AppConfig,
    {
       provide: PERFECT_SCROLLBAR_CONFIG,
       useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG
    },
    {provide: MAT_DATE_LOCALE, useValue: 'es-MX'}
  ],
  entryComponents: [ DetailsPaymentsComponent ],
  bootstrap: [AppComponent]
})
export class AppModule { }
